import { useLazyQuery, useQuery, useTranslation } from '@gimlite/common';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Query, QueryTechnicalEventArgs } from '../../client/graphql';
import { incidentGql } from '../../common/gql/incident.gql';
import { iconGlobalEntity } from '../../common/mapper/icon.mapper';
import { StringFn, parseTokens } from '../../common/utils/tokens-2-payload';
import { ProfilDetails } from '../../components/profil-details/profil-details.component';
import { IncidentAction } from '../action/incident.action';
import { IncidentLineType } from '../line/incident.line';
import { BadgeType, Empty, FormType, Widget, Zone } from '@gimlite/ui-v1';

class Static {
  static tokenMap = {
    DATE: (payload: any, t: StringFn) =>
      DateTime.fromISO(payload.date).toFormat('dd.LL.yy HH:mm'),
    UUU: (payload: any, t: StringFn) =>
      t(`global-providers-${payload.credential?.provider?.toLowerCase()}`),
    RFID: (payload: any, t: StringFn) =>
      payload.credential?.value.toUpperCase(),
    EID: (payload: any, t: StringFn) => payload.credential?.value.toUpperCase(),
    PL: (payload: any, t: StringFn) => payload.credential?.value.toUpperCase(),
    PN: (payload: any, t: StringFn) => payload.spacesAllocated,
    NNE: (payload: any, t: StringFn) => payload.openedSessions?.length,
    NDAY: (payload: any, t: StringFn) =>
      payload.rules?.cycleControl?.pastDaysCount,
    NC: (payload: any, t: StringFn) => payload.contractId,
  };

  static statusBadgeColorMap: Record<
    IncidentLineType.Data.Status,
    BadgeType.Config.Color
  > = {
    information: 'disabled',
    warning: 'warn',
    alarm: 'error',
  };
}

export type IncidentDetailsProps = {
  widget?: {
    title?: string;
  };
  technicalEventId: string | null;
};

export const IncidentDetails = ({
  technicalEventId,
  widget,
}: IncidentDetailsProps) => {
  const [technicalEventCall, technicalEventState] = useLazyQuery<
    {
      technicalEvent: Query['technicalEvent'];
    },
    QueryTechnicalEventArgs
  >(incidentGql, {
    variables: {
      id: technicalEventId || '',
    },
    socket: {
      'sessions:updated': () => true,
    },
  });

  const technicalEvent = useMemo(() => {
    if (!technicalEventState.data) return null;

    return technicalEventState.data.technicalEvent;
  }, [technicalEventState.data]);

  const { t } = useTranslation();

  const [form, setForm] = useState<FormType.Data.Value>();

  const badges = [
    {
      color:
        Static.statusBadgeColorMap[
          !!technicalEvent && technicalEvent.criticity
            ? technicalEvent.criticity
            : 'information'
        ],
      text: t(`${technicalEvent?.criticity || 'information'}`),
    },
  ];

  if (technicalEvent && technicalEvent.archived)
    badges.push({
      text: t('archived'),
      color: 'disabled',
    });

  useEffect(() => {
    if (!!technicalEvent) {
      setForm({
        problem:
          t(`incident-title-${technicalEvent?.key}`) +
          `${
            technicalEvent?.payload?.accessPoint?.shortCode
              ? ` : ${t('access-point')} ${
                  technicalEvent?.payload?.accessPoint?.shortCode
                }`
              : ''
          }`,
        description: parseTokens(
          t(`incident-${technicalEvent?.key}`),
          {
            date: technicalEvent?.createdAt,
            ...technicalEvent?.payload,
          },
          Static.tokenMap,
          t,
        ),
      });
    }
  }, [technicalEvent, setForm]);

  useEffect(() => {
    if (technicalEventId) {
      technicalEventCall({ variables: { id: technicalEventId } });
    } else {
      technicalEventState.resetData();
    }
  }, [technicalEventId]);

  return (
    <Widget.Group
      config={{
        title: widget?.title,
        backtitle: !!widget?.title,
      }}
      state={{
        loading: technicalEventState.loading,
        error: technicalEventState.error,
        refetch: () => technicalEventState.refetch(),
        showPermanentRefetch: false,
      }}
    >
      {technicalEvent && technicalEventId && form ? (
        <Zone
          config={{
            zones: [['profil']],
            rows: ['min-content'],
            columns: ['1fr'],
          }}
        >
          <Zone.Area config={{ area: 'profil' }}>
            <Widget>
              <ProfilDetails
                handleEvent={{
                  submit: (form) => setForm(form),
                }}
                data={{
                  icon: iconGlobalEntity['incident'],
                  form: form,
                  badges: badges,
                }}
                config={{
                  form: {
                    title1: {
                      name: 'problem',
                      placeholder: t('problem'),
                      edit: false,
                    },
                    description1: {
                      name: 'description',
                      placeholder: t('description'),
                      edit: false,
                    },
                  },

                  actions: (
                    <IncidentAction
                      config={{ size: 'xlarge' }}
                      data={{
                        technicalEventId,
                        payload: technicalEvent.payload,
                      }}
                    />
                  ),
                }}
              />
            </Widget>
          </Zone.Area>
        </Zone>
      ) : (
        <Empty
          config={{
            mode: { name: 'select', text: `${t('selectIncident')} ...` },
          }}
        ></Empty>
      )}
    </Widget.Group>
  );
};
